import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

type ReferralLevel = {
  level: number;
  count: number;
  earned: number;
  percentage: number;
};

type ReferralUser = {
  user_id: string;
  referrals_count: number;
  total_deposits: number;
  earned: number;
};

type ReferralStats = {
  total_referrals: number;
  total_earned: number;
  tier: string;
  tier_name: string;
  levels: ReferralLevel[];
  referrals: ReferralUser[];
};

const FriendsScreen = () => {
  const userId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
  const { t, language } = useLanguage();
  
  const [referralLink, setReferralLink] = useState<string>('');
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    // Задержка для предотвращения флешинга
    const timer = setTimeout(() => {
      loadReferralData();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReferralData = async (retryCount = 3): Promise<void> => {
    setLoading(true);
    
    try {
      console.log('📡 Загрузка реферальных данных...', { userId, retryCount });
      
      // Получаем реферальную ссылку
      const linkResponse = await axios.get(`${API_BASE_URL}/referral/link`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      console.log('✅ Реферальная ссылка получена');
      
      if (linkResponse.data && linkResponse.data.referral_link) {
        setReferralLink(linkResponse.data.referral_link);
      } else {
        setReferralLink('Ошибка генерации ссылки');
      }

      // Получаем статистику
      const statsResponse = await axios.get(`${API_BASE_URL}/referral/stats`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      console.log('✅ Статистика рефералов получена');
      
      const statsData = statsResponse.data || {};
      setStats({
        total_referrals: statsData.total_referrals || 0,
        total_earned: statsData.total_earned || 0,
        tier: statsData.tier || 'standard',
        tier_name: statsData.tier_name || 'Стандарт',
        levels: Array.isArray(statsData.levels) ? statsData.levels : [],
        referrals: Array.isArray(statsData.referrals) ? statsData.referrals : []
      });
    } catch (error: any) {
      console.error('❌ Ошибка загрузки реферальных данных:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        retryCount
      });
      
      // Retry при таймауте или сетевых ошибках
      if (retryCount > 0 && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
        console.log(`🔄 Повторная попытка через 2 сек... (осталось попыток: ${retryCount})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadReferralData(retryCount - 1);
      }
      
      // Устанавливаем пустую статистику чтобы не ломать UI
      setStats({
        total_referrals: 0,
        total_earned: 0,
        tier: 'standard',
        tier_name: 'Стандарт',
        levels: [],
        referrals: []
      });
      
      // Устанавливаем fallback ссылку
      setReferralLink('https://t.me/ton_treasurechestbot');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = () => {
    if ((window as any).Telegram?.WebApp) {
      const text = language === 'ru' 
        ? `🏰 Присоединяйся к TON Farm!\n\nНачни зарабатывать TON прямо сейчас!\n\n${referralLink}`
        : `🏰 Join TON Farm!\n\nStart earning TON right now!\n\n${referralLink}`;
      (window as any).Telegram.WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`);
    } else {
      handleCopyLink();
    }
  };

  return (
    <div
      className="pb-20 px-3 min-h-screen bg-cover bg-center bg-fixed"
      style={{ backgroundImage: 'url(/assets/treasures-bg.jpg)' }}
    >
      <div className="space-y-4 mt-4">
        {/* Loading */}
        {loading && !stats && (
          <div className="card bg-white/10 text-white text-center">
            <div className="text-4xl mb-3">⏳</div>
            <div className="text-lg">{language === 'ru' ? 'Загрузка...' : 'Loading...'}</div>
          </div>
        )}

        {/* Блок амбассадорства */}
        <div className="card bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-300/30 text-white">
          <div className="text-xl font-bold mb-3 text-center">{t.ambassadorProgram}</div>
          
          <p className="text-sm mb-4 text-center opacity-90">
            {t.ambassadorText}
          </p>
          
          <button
            className="w-full py-3 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all shadow-soft"
            onClick={() => {
              if ((window as any).Telegram?.WebApp) {
                (window as any).Telegram.WebApp.openTelegramLink('https://t.me/treasuretonsup');
              } else {
                window.open('https://t.me/treasuretonsup', '_blank');
              }
            }}
          >
            {t.becomePartner}
          </button>
        </div>

        {/* Реферальная ссылка */}
        <div className="card bg-white/10 text-white">
          <div className="text-xl font-bold mb-4 text-center">{t.inviteFriends}</div>
          
          <div className="bg-white/10 rounded-lg p-3 mb-3 break-all text-sm">
            <div className="opacity-70 text-xs mb-1">{t.yourReferralLink}</div>
            <div className="font-mono">{referralLink || (language === 'ru' ? 'Загрузка...' : 'Loading...')}</div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleCopyLink}
              disabled={!referralLink}
              className="py-2 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors"
            >
              {copied ? t.copiedButton : t.copyButton}
            </button>
            
            <button
              onClick={handleShare}
              disabled={!referralLink}
              className="py-2 rounded-lg font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors"
            >
              {t.shareButton}
            </button>
          </div>
        </div>

        {/* Общая статистика */}
        {stats && (
          <div className="card bg-white/10 text-white">
            <div className="text-xl font-bold mb-4 text-center">{t.yourStats}</div>
            
            {/* Tier Status Badge */}
            {stats.tier && (stats.tier === 'partner' || stats.tier === 'vip') && (
              <div className="mb-4 p-4 rounded-lg text-center bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-white/20">
                <div className="text-sm opacity-80 mb-2">{language === 'ru' ? 'Ваш статус' : 'Your Status'}</div>
                <div className={`text-2xl font-bold ${
                  stats.tier === 'vip' ? 'text-yellow-300' :
                  stats.tier === 'partner' ? 'text-blue-300' :
                  'text-white'
                }`}>
                  {stats.tier === 'vip' ? '🌟 ' : stats.tier === 'partner' ? '💎 ' : ''}
                  {stats.tier_name}
                </div>
                <div className="text-xs opacity-70 mt-2">
                  {language === 'ru' ? 'У вас эксклюзивные условия!' : 'You have exclusive conditions!'}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-sm opacity-80">{t.totalReferrals}</div>
                <div className="text-2xl font-bold text-yellow-400">{stats.total_referrals || 0}</div>
              </div>
              
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-sm opacity-80">{t.totalEarned}</div>
                <div className="text-2xl font-bold text-green-400">{(stats.total_earned || 0).toFixed(2)} TON</div>
              </div>
            </div>
          </div>
        )}

        {/* Таблица уровней */}
        {stats && stats.levels && stats.levels.length > 0 && (
          <div className="card bg-white/10 text-white">
            <div className="text-lg font-bold mb-3">{t.byLevels}</div>
            
            <div className="space-y-2">
              {stats.levels.map((level) => (
                <div key={level.level} className="bg-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">{t.level} {level.level || 0}</span>
                    <span className="text-xs bg-yellow-500/30 px-2 py-1 rounded">{Math.round(level.percentage || 0)}%</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="opacity-70">{t.referralsCount}</div>
                      <div className="font-semibold">{level.count || 0}</div>
                    </div>
                    <div>
                      <div className="opacity-70">{t.earned}</div>
                      <div className="font-semibold text-green-400">{(level.earned || 0).toFixed(2)} TON</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Список рефералов */}
        {stats && stats.referrals && stats.referrals.length > 0 && (
          <div className="card bg-white/10 text-white">
            <div className="text-lg font-bold mb-3">{t.yourReferrals}</div>
            
            <div className="space-y-2">
              {stats.referrals.map((ref, index) => (
                <div key={index} className="bg-white/10 rounded-lg p-3">
                  <div className="text-xs opacity-70 mb-2 font-mono truncate">
                    ID: {ref.user_id}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="opacity-70">{t.referralsLabel}</div>
                      <div className="font-semibold">{ref.referrals_count || 0}</div>
                    </div>
                    <div>
                      <div className="opacity-70">{t.deposits}</div>
                      <div className="font-semibold">{(ref.total_deposits || 0).toFixed(1)} TON</div>
                    </div>
                    <div>
                      <div className="opacity-70">{t.earnedLabel}</div>
                      <div className="font-semibold text-green-400">{(ref.earned || 0).toFixed(2)} TON</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Пустое состояние */}
        {stats && stats.total_referrals === 0 && (
          <div className="card bg-white/10 text-white text-center">
            <div className="text-4xl mb-3">🎯</div>
            <div className="text-lg font-semibold mb-2">{t.noReferrals}</div>
            <div className="text-sm opacity-80">
              {t.noReferralsText}
            </div>
          </div>
        )}

        {/* Как работает */}
        <div className="card bg-white/10 text-white">
          <div className="text-lg font-bold mb-3">{t.howItWorks}</div>
          
          <div className="space-y-3 text-sm">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500/30 flex items-center justify-center font-bold">1</div>
              <div>
                <div className="font-semibold">{t.level1}</div>
                <div className="opacity-80 text-xs">{t.level1Text}</div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500/30 flex items-center justify-center font-bold">2</div>
              <div>
                <div className="font-semibold">{t.level2}</div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500/30 flex items-center justify-center font-bold">3</div>
              <div>
                <div className="font-semibold">{t.level3}</div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-xs">
            {t.bonusInfo}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendsScreen;
